import React from 'react';
import { IconButton, cn } from '../ui';

/**
 * Top of the lyrics size range. Deliberately far past a comfortable reading size —
 * the reader may be a group singing off a TV, or someone who simply needs the type
 * very large, and the lyrics have no zoom affordance of their own.
 */
export const MAX_LYRICS_FONT_SIZE = 65;

/**
 * How far one press moves, given where the reader already is.
 *
 * A fixed 2px step is right around reading sizes — it is the difference between
 * "comfortable" and "slightly too small" — but it leaves the top of the range 20+
 * presses away. So the step grows with the value, keeping each press a roughly
 * *proportional* jump rather than an absolute one. Pass `step` to override.
 */
export function fontStepFor(value: number): number {
  if (value < 32) return 2;
  if (value < 56) return 4;
  return 8;
}

/** The size one press in `direction` lands on, clamped to the range. */
export function nextFontSize(
  value: number,
  direction: 1 | -1,
  min: number,
  max: number,
  step?: number
): number {
  // Stepping down asks for the step of the size being *left behind* (hence the -1),
  // so a rung is walked at the same granularity in both directions.
  const delta = step ?? fontStepFor(direction === 1 ? value : value - 1);
  return Math.min(max, Math.max(min, value + direction * delta));
}

export interface FontSizeControlProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  /** Fixed step. Omit for the size-dependent ladder — see `fontStepFor`. */
  step?: number;
  className?: string;
}

export const FontSizeControl: React.FC<FontSizeControlProps> = ({
  value,
  onChange,
  min = 14,
  max = MAX_LYRICS_FONT_SIZE,
  step,
  className,
}) => {
  return (
    <div className={cn('inline-flex items-center gap-1 rounded-full bg-bg-3 border border-light px-1 py-0.5', className)}>
      <IconButton
        ariaLabel="Decrease lyrics font"
        size="sm"
        variant="ghost"
        disabled={value <= min}
        onClick={() => onChange(nextFontSize(value, -1, min, max, step))}
        icon={<span className="text-body font-semibold">A−</span>}
      />
      <span className="px-1 font-mono text-small text-text-secondary tabular-nums w-8 text-center">{value}</span>
      <IconButton
        ariaLabel="Increase lyrics font"
        size="sm"
        variant="ghost"
        disabled={value >= max}
        onClick={() => onChange(nextFontSize(value, 1, min, max, step))}
        icon={<span className="text-body font-semibold">A+</span>}
      />
    </div>
  );
};
